import React, { useState, useEffect, useRef } from 'react';
import { modelRegistryAPI, getModelInfo, getPreprocessLog, getDecisionThreshold, updateDecisionThreshold, getRocImageUrlByModelId, getConfusionImageUrlByModelId, getFeatureImportanceImageUrlByModelId, getModelPerformanceImageUrlByModelId, getFeatureImportanceDataByModelId, getModelPerformanceDataByModelId, exportComprehensiveReport, downloadFile, getModelSummary, getEvaluationFlags } from '../api';
import { InfoTooltip, LoadingState, ErrorState, EmptyState } from './shared/UIComponents';
import { useActiveModel } from '../context/ActiveModelContext';

const ModelRegistry = () => {
  const [registryInfo, setRegistryInfo] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const { activeModel, setActiveModel, clearActiveModel } = useActiveModel();
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDataset, setFilterDataset] = useState('all');
  const [filterModelType, setFilterModelType] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Decision threshold (binary classification; no retraining)
  const [decisionThreshold, setDecisionThreshold] = useState(null);
  const [thresholdUpdating, setThresholdUpdating] = useState(false);

  // Evaluation transparency: summary and flags (fetched when a model is selected)
  const [evaluationSummary, setEvaluationSummary] = useState(null);
  const [evaluationFlags, setEvaluationFlags] = useState([]);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'repair' | 'delete' | null
  const [pendingDeleteModelId, setPendingDeleteModelId] = useState(null);
  const [showCleanupPrompt, setShowCleanupPrompt] = useState(false);
  const [cleanupDaysInput, setCleanupDaysInput] = useState('30');
  const [validatingModelId, setValidatingModelId] = useState(null);
  const registryRootRef = useRef(null);
  const savedScrollRef = useRef(null);

  const getScrollableParent = (el) => {
    if (!el) return window;
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const canScroll = /(auto|scroll)/.test(style.overflowY);
      if (canScroll && parent.scrollHeight > parent.clientHeight) return parent;
      parent = parent.parentElement;
    }
    return window;
  };

  const captureScrollPosition = () => {
    const container = getScrollableParent(registryRootRef.current);
    savedScrollRef.current = {
      container,
      top: container === window ? window.scrollY : container.scrollTop,
    };
  };

  const restoreScrollPosition = () => {
    if (!savedScrollRef.current) return;
    const { container, top } = savedScrollRef.current;
    requestAnimationFrame(() => {
      if (container === window) {
        window.scrollTo({ top });
      } else {
        container.scrollTop = top;
      }
    });
  };

  useEffect(() => {
    loadRegistryData();
    loadHealthStatus();
  }, []);

  // Fetch evaluation summary and flags when a model is selected
  useEffect(() => {
    if (!activeModel?.id || !activeModel?.dataset_name) {
      setEvaluationSummary(null);
      setEvaluationFlags([]);
      return;
    }
    setEvaluationLoading(true);
    Promise.all([
      getModelSummary(activeModel.dataset_name, activeModel.id),
      getEvaluationFlags(activeModel.dataset_name, activeModel.id),
    ]).then(([summary, flags]) => {
      setEvaluationSummary(typeof summary === 'string' ? summary : (summary?.summary ?? null));
      setEvaluationFlags(Array.isArray(flags) ? flags : []);
    }).catch(() => {
      setEvaluationSummary(null);
      setEvaluationFlags([]);
    }).finally(() => setEvaluationLoading(false));
  }, [activeModel?.id, activeModel?.dataset_name]);

  // Keep local selection in sync with context
  useEffect(() => {
    setSelectedModelId(activeModel?.id || null);
  }, [activeModel]);

  // Load decision threshold when active model is a classifier
  useEffect(() => {
    if (!activeModel?.id) {
      setDecisionThreshold(null);
      return;
    }
    const taskType = activeModel.metadata?.task_type || activeModel.task_type;
    if (taskType !== 'classification') {
      setDecisionThreshold(null);
      return;
    }
    const fromModel = activeModel.decision_threshold ?? activeModel.metadata?.decision_threshold;
    if (fromModel != null) {
      setDecisionThreshold(Number(fromModel));
      return;
    }
    getDecisionThreshold(activeModel.id)
      .then((res) => setDecisionThreshold(Number(res.decision_threshold ?? 0.5)))
      .catch(() => setDecisionThreshold(0.5));
  }, [activeModel?.id, activeModel?.decision_threshold, activeModel?.metadata?.decision_threshold, activeModel?.metadata?.task_type, activeModel?.task_type]);

  const loadRegistryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modelRegistryAPI.getModelsRegistry();
      setRegistryInfo((data && data.registry_info) || {});
      setModels((data && data.models) || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHealthStatus = async () => {
    try {
      const health = await modelRegistryAPI.getRegistryHealth();
      setHealthStatus(health);
    } catch (err) {
      console.error('Failed to load health status:', err);
    }
  };

  const handleValidateModels = async () => {
    captureScrollPosition();
    try {
      setActionLoading(true);
      const result = await modelRegistryAPI.validateAllModels();
      setActionFeedback({
        type: 'success',
        message: `Validation completed: ${result.valid_models} valid, ${result.corrupted_models} corrupted, ${result.missing_models} missing.`,
      });
      await loadHealthStatus();
      await loadRegistryData();
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `Validation failed: ${err.message}`,
      });
    } finally {
      setActionLoading(false);
      restoreScrollPosition();
    }
  };

  const handleValidateSingleModel = async (modelId) => {
    captureScrollPosition();
    try {
      setValidatingModelId(modelId);
      const result = await modelRegistryAPI.validateModelById(modelId);
      setActionFeedback({
        type: 'success',
        message: `Model ${result.model_id} validated as ${result.status}.`,
      });
      await loadHealthStatus();
      await loadRegistryData();
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `Validation failed for ${modelId}: ${err.message}`,
      });
    } finally {
      setValidatingModelId(null);
      restoreScrollPosition();
    }
  };

  const executeRepairRegistry = async () => {
    captureScrollPosition();
    try {
      setActionLoading(true);
      const result = await modelRegistryAPI.repairRegistry();
      setActionFeedback({
        type: 'success',
        message: `Registry repaired: ${result.initial_model_count} -> ${result.final_model_count} models.`,
      });
      await loadHealthStatus();
      await loadRegistryData();
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `Repair failed: ${err.message}`,
      });
    } finally {
      setActionLoading(false);
      restoreScrollPosition();
    }
  };

  const handleRepairRegistry = async () => {
    setConfirmAction('repair');
  };

  const executeDeleteModel = async (modelId) => {
    captureScrollPosition();
    try {
      await modelRegistryAPI.deleteModel(modelId);
      await loadRegistryData();
      await loadHealthStatus();
      if (activeModel?.id === modelId) {
        clearActiveModel();
      }
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `Delete failed: ${err.message}`,
      });
    }
    restoreScrollPosition();
  };

  const handleDeleteModel = async (modelId) => {
    setPendingDeleteModelId(modelId);
    setConfirmAction('delete');
  };

  const fetchAsDataUrl = async (url) => {
    try {
      // Add cache-busting to avoid stale or cached images
      const cacheBust = url.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`;
      const res = await fetch(`${url}${cacheBust}`, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (_) {
      // Return null so caller can fall back to the raw URL
      return null;
    }
  };

  const buildHtmlReport = ({ title, modelId, timestamp, sections }) => `<!doctype html><html><head><meta charset="utf-8"/><title>${title}</title><style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;color:#111}h1{margin:0 0 16px}h2{margin:24px 0 8px}section{margin-bottom:24px}.report-img{max-width:640px;width:100%;height:auto;border:1px solid #eee;border-radius:8px;padding:6px;background:#fff}code,pre{background:#f7f7f7;padding:8px;border-radius:6px;display:block;white-space:pre-wrap}table{border-collapse:collapse}td,th{border:1px solid #ddd;padding:6px 8px;text-align:left}</style></head><body><h1>${title}</h1><div><strong>Model ID:</strong> ${modelId}</div><div><strong>Exported:</strong> ${timestamp}</div>${sections.join('')} </body></html>`;

  const buildBarChartSvg = (items, labelKey, valueKey, { width = 600, height = 350, title = '' } = {}) => {
    if (!Array.isArray(items) || items.length === 0) return '';
    const padding = { top: 30, right: 20, bottom: 120, left: 60 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;
    const topItems = items.slice(0, 20);
    const maxVal = Math.max(...topItems.map(i => Number(i[valueKey]) || 0), 1);
    const barW = chartW / topItems.length;
    const bars = topItems.map((it, idx) => {
      const v = Number(it[valueKey]) || 0;
      const h = (v / maxVal) * chartH;
      const x = padding.left + idx * barW + 4;
      const y = padding.top + (chartH - h);
      const label = String(it[labelKey]).replace(/&/g, '&amp;').replace(/</g, '&lt;');
      return `<rect x="${x}" y="${y}" width="${Math.max(2, barW - 8)}" height="${h}" fill="#1d4ed8" />\n` +
             `<text x="${x + (barW - 8) / 2}" y="${height - padding.bottom / 2}" font-size="10" text-anchor="middle" transform="rotate(45 ${x + (barW - 8) / 2},${height - padding.bottom / 2})">${label}</text>`;
    }).join('\n');
    const axis = `<line x1="${padding.left}" y1="${padding.top + chartH}" x2="${width - padding.right}" y2="${padding.top + chartH}" stroke="#666" />` +
                 `<line x1="${padding.left}" y1="${padding.top}" x2="${padding.left}" y2="${padding.top + chartH}" stroke="#666" />`;
    const titleText = title ? `<text x="${width / 2}" y="20" text-anchor="middle" font-size="16" font-weight="600">${title}</text>` : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">\n${titleText}\n${axis}\n${bars}\n</svg>`;
  };

  const handleExportReport = async () => {
    const modelId = activeModel?.id;
    const datasetName = activeModel?.dataset_name;
    if (!modelId || !datasetName) return;
    try {
      setExporting(true);
      const now = new Date();
      const ts = now.toISOString().replace(/[:.]/g, '-');
      const filenameBase = `report_${modelId}_${ts}`;

      // Metadata and preprocess log
      let metadata = null;
      try { metadata = await getModelInfo(modelId); } catch (_) {}
      let preprocessLog = null;
      try { preprocessLog = await getPreprocessLog(modelId); } catch (_) {}

      // Image URLs (best-effort)
      const rocUrl = getRocImageUrlByModelId(datasetName, modelId);
      const confUrl = getConfusionImageUrlByModelId(datasetName, modelId);
      const fiUrl = getFeatureImportanceImageUrlByModelId(datasetName, modelId);
      const mpUrl = getModelPerformanceImageUrlByModelId(datasetName, modelId);

      const [rocImg, confImg, fiImg, mpImg] = await Promise.all([
        fetchAsDataUrl(rocUrl),
        fetchAsDataUrl(confUrl),
        fetchAsDataUrl(fiUrl),
        fetchAsDataUrl(mpUrl),
      ]);

      const sections = [];
      sections.push(`<section><h2>Model Metadata</h2><pre>${metadata ? JSON.stringify(metadata, null, 2) : 'Unavailable'}</pre></section>`);
      sections.push(`<section><h2>Preprocess Log</h2><pre>${preprocessLog ? JSON.stringify(preprocessLog, null, 2) : 'Unavailable'}</pre></section>`);
      if (rocImg || rocUrl) sections.push(`<section><h2>ROC Curve</h2><img class="report-img" src="${rocImg || rocUrl}" alt="ROC Curve"/></section>`);
      if (confImg || confUrl) sections.push(`<section><h2>Confusion Matrix</h2><img class="report-img" src="${confImg || confUrl}" alt="Confusion Matrix"/></section>`);
      // For feature importance and model performance, if data URL is unavailable, prefer inline SVG over raw URL to avoid broken images
      if (fiImg) {
        sections.push(`<section><h2>Feature Importance</h2><img class="report-img" src="${fiImg}" alt="Feature Importance"/></section>`);
      } else {
        // Fallback: fetch model-scoped data and render inline SVG
        try {
          const res = await getFeatureImportanceDataByModelId(datasetName, modelId);
          const items = Array.isArray(res?.data) ? res.data : (res?.data?.items || res?.items || []);
          const normalized = items.map((d) => ({ feature: d.feature || d.name || d[0] || 'Feature', importance: Number(d.importance ?? d.value ?? d[1] ?? 0) }));
          const svg = buildBarChartSvg(normalized, 'feature', 'importance', { title: 'Feature Importance' });
          if (svg) sections.push(`<section><h2>Feature Importance</h2>${svg}</section>`);
        } catch (_) {}
      }

      if (mpImg) {
        sections.push(`<section><h2>Model Performance</h2><img class="report-img" src="${mpImg}" alt="Model Performance"/></section>`);
      } else {
        // Fallback: fetch model-scoped data and render inline SVG
        try {
          const res = await getModelPerformanceDataByModelId(datasetName, modelId);
          const items = Array.isArray(res?.data) ? res.data : (res?.data?.items || res?.items || []);
          const normalized = items.map((d) => ({ metric: d.metric || d.name || d[0] || 'Metric', value: Number(d.value ?? d.score ?? d[1] ?? 0) }));
          const svg = buildBarChartSvg(normalized, 'metric', 'value', { title: 'Model Performance' });
          if (svg) sections.push(`<section><h2>Model Performance</h2>${svg}</section>`);
        } catch (_) {}
      }

      const html = buildHtmlReport({
        title: `Model Report for ${datasetName}`,
        modelId,
        timestamp: now.toLocaleString(),
        sections,
      });

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filenameBase}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdfReport = async () => {
    const modelId = activeModel?.id;
    if (!modelId) {
      setActionFeedback({
        type: 'error',
        message: 'Please select a model to export.',
      });
      return;
    }

    try {
      setExporting(true);
      const { blob, filename } = await exportComprehensiveReport(modelId);
      downloadFile(blob, filename);
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `PDF export failed: ${err.message}`,
      });
    } finally {
      setExporting(false);
    }
  };

  const executeCleanupOldModels = async (days) => {
    captureScrollPosition();
    try {
      setActionLoading(true);
      const result = await modelRegistryAPI.cleanupOldModels(parseInt(days, 10));
      setActionFeedback({
        type: 'success',
        message: `Cleanup completed: ${result.deleted_count} models deleted.`,
      });
      await loadRegistryData();
      await loadHealthStatus();
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: `Cleanup failed: ${err.message}`,
      });
    } finally {
      setActionLoading(false);
      restoreScrollPosition();
    }
  };

  const handleCleanupOldModels = async () => {
    setShowCleanupPrompt(true);
  };

  // Filter and sort models
  const filteredAndSortedModels = models.filter(model => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      model.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.dataset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_type.toLowerCase().includes(searchTerm.toLowerCase());

    // Dataset filter
    const matchesDataset = filterDataset === 'all' || model.dataset_name === filterDataset;

    // Model type filter
    const matchesModelType = filterModelType === 'all' || model.model_type === filterModelType;

    return matchesSearch && matchesDataset && matchesModelType;
  }).sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle nested values
    if (sortBy === 'accuracy' && a.metrics) {
      aValue = a.metrics.accuracy || 0;
      bValue = b.metrics.accuracy || 0;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Pagination calculations
  const totalModels = filteredAndSortedModels.length;
  const totalPages = Math.ceil(totalModels / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentModels = filteredAndSortedModels.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Handle pagination navigation
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Get unique datasets and model types for filters
  const uniqueDatasets = [...new Set(models.map(m => m.dataset_name))];
  const uniqueModelTypes = [...new Set(models.map(m => m.model_type))];

  const handleSelectModel = async (model) => {
    setSelectedModelId(model.id);
    try {
      // Load full metadata for the selected model
      const full = await modelRegistryAPI.getModelInfo(model.id).catch(() => null);
      // Normalize fields likely to hold normalization data
      const merged = full ? { ...model, ...full } : model;

      // Pull expanded metadata with sensible fallbacks for nested structures
      const normalization_method = merged.normalization_method
        ?? merged.metadata?.normalization_method
        ?? merged.preprocessing?.normalization_method
        ?? null;

      const version = merged.version
        ?? merged.metadata?.version
        ?? null;

      const preprocessing_steps = merged.preprocessing_steps
        ?? merged.preprocessing?.steps
        ?? merged.metadata?.preprocessing_steps
        ?? null;

      const batch_correction_method = merged.batch_correction_method
        ?? merged.preprocessing?.batch_correction_method
        ?? merged.metadata?.batch_correction_method
        ?? null;

      const feature_set = merged.feature_set
        ?? merged.metadata?.feature_set
        ?? null;

      const data_signature = merged.data_signature
        ?? merged.metadata?.data_signature
        ?? null;

      setActiveModel({
        ...merged,
        normalization_method,
        version,
        preprocessing_steps,
        batch_correction_method,
        feature_set,
        data_signature,
      });
    } catch (e) {
      setActiveModel(model);
    }
  };

  const isSelected = (modelId) => selectedModelId === modelId;

  const formatFileSize = (sizeMb) => {
    if (sizeMb == null || isNaN(sizeMb)) return 'N/A';
    if (sizeMb < 1) return `${(sizeMb * 1024).toFixed(1)} KB`;
    return `${Number(sizeMb).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getModelStatusBadge = (model) => {
    const status = model.status || 'unknown';
    
    return (
      <span className={`model-registry__status-badge model-registry__status-badge--${status}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) return <LoadingState message="Loading model registry..." />;
  if (error) return <ErrorState message={error} onRetry={loadRegistryData} />;

  return (
    <div className="model-registry" ref={registryRootRef}>
      <div className="model-registry__header">
        <h2 className="model-registry__title">Model Registry</h2>
        <InfoTooltip title="Model Registry" content="Manage and monitor all trained machine learning models." />
      </div>
      <div style={{ marginBottom: 12, padding: 10, border: '1px solid #dbeafe', borderRadius: 8, background: '#f8fbff', fontSize: 13 }}>
        <strong>Validation guidance:</strong> confirm model health, review evaluation flags, and tune decision thresholds before prediction/pathway interpretation/export.
      </div>

      {/* Health Status */}
      {healthStatus && (
        <div className="model-registry__health">
          <h3 className="model-registry__health-title">Registry Health Status</h3>
          <div className="model-registry__health-grid">
            <div className="model-registry__health-item">
              <span className="model-registry__health-label">Total Models:</span>
              <div className="model-registry__health-value model-registry__health-value--total">{healthStatus.total_models}</div>
            </div>
            <div className="model-registry__health-item">
              <span className="model-registry__health-label">Valid:</span>
              <div className="model-registry__health-value model-registry__health-value--valid">{healthStatus.valid_models}</div>
            </div>
            <div className="model-registry__health-item">
              <span className="model-registry__health-label">Corrupted:</span>
              <div className="model-registry__health-value model-registry__health-value--corrupted">{healthStatus.corrupted_models}</div>
            </div>
            <div className="model-registry__health-item">
              <span className="model-registry__health-label">Missing:</span>
              <div className="model-registry__health-value model-registry__health-value--missing">{healthStatus.missing_models}</div>
            </div>
          </div>
          {healthStatus.recommendations && healthStatus.recommendations.length > 0 && (
            <div className="model-registry__health-recommendations">
              <span className="model-registry__health-recommendations-label">Recommendations:</span>
              <ul className="model-registry__health-recommendations-list">
                {healthStatus.recommendations.map((rec, idx) => (
                  <li key={idx}>• {rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Registry Statistics */}
      {registryInfo && (
        <div className="model-registry__stats">
          <h3 className="model-registry__stats-title">Registry Statistics</h3>
          <div className="model-registry__stats-grid">
            <div className="model-registry__stats-item">
              <span className="model-registry__stats-label">Total Models:</span>
              <div className="model-registry__stats-value">{registryInfo.total_models}</div>
            </div>
            <div className="model-registry__stats-item">
              <span className="model-registry__stats-label">Total Size:</span>
              <div className="model-registry__stats-value">{formatFileSize(registryInfo.total_size_mb)}</div>
            </div>
            <div className="model-registry__stats-item">
              <span className="model-registry__stats-label">Datasets:</span>
              <div className="model-registry__stats-value">{Object.keys(registryInfo.by_dataset || {}).length}</div>
            </div>
            <div className="model-registry__stats-item">
              <span className="model-registry__stats-label">Model Types:</span>
              <div className="model-registry__stats-value">{Object.keys(registryInfo.by_model_type || {}).length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="model-registry__actions">
        <button
          type="button"
          onClick={handleValidateModels}
          disabled={actionLoading}
          className="btn btn--primary"
        >
          {actionLoading ? 'Validating...' : 'Validate All Models'}
        </button>
        <button
          type="button"
          onClick={handleRepairRegistry}
          disabled={actionLoading}
          className="btn btn--secondary"
        >
          Repair Registry
        </button>
        <button
          type="button"
          onClick={handleCleanupOldModels}
          disabled={actionLoading}
          className="btn btn--secondary"
        >
          Cleanup Old Models
        </button>
        <button
          type="button"
          onClick={() => {loadRegistryData(); loadHealthStatus();}}
          className="btn btn--outline"
        >
          Refresh
        </button>
      </div>

      {actionFeedback && (
        actionFeedback.type === 'error' ? (
          <div>
            <ErrorState
              message={actionFeedback.message}
              onRetry={() => setActionFeedback(null)}
            />
            <div style={{ marginBottom: 16 }}>
              <button type="button" className="btn btn--outline" onClick={() => setActionFeedback(null)}>
                Close message
              </button>
            </div>
          </div>
        ) : (
          <div className="model-registry__message model-registry__message--success" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>{actionFeedback.message}</span>
            <button type="button" className="btn btn--outline" onClick={() => setActionFeedback(null)}>
              Close message
            </button>
          </div>
        )
      )}

      {confirmAction === 'repair' && (
        <div className="model-registry__message model-registry__message--info" style={{ marginBottom: 16 }}>
          <p style={{ marginTop: 0, marginBottom: 8 }}>
            This will validate all models and remove invalid ones. Continue?
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={actionLoading}
              onClick={() => {
                setConfirmAction(null);
                executeRepairRegistry();
              }}
            >
              Confirm Repair
            </button>
            <button
              type="button"
              className="btn btn--outline"
              disabled={actionLoading}
              onClick={() => setConfirmAction(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmAction === 'delete' && (
        <div className="model-registry__message model-registry__message--info" style={{ marginBottom: 16 }}>
          <p style={{ marginTop: 0, marginBottom: 8 }}>
            Are you sure you want to delete model <strong>{pendingDeleteModelId}</strong>?
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => {
                const id = pendingDeleteModelId;
                setConfirmAction(null);
                setPendingDeleteModelId(null);
                if (id) executeDeleteModel(id);
              }}
            >
              Confirm Delete
            </button>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                setConfirmAction(null);
                setPendingDeleteModelId(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showCleanupPrompt && (
        <div className="model-registry__message model-registry__message--info" style={{ marginBottom: 16 }}>
          <label htmlFor="cleanup-days-input" style={{ display: 'block', marginBottom: 8 }}>
            Delete models older than how many days?
          </label>
          <input
            id="cleanup-days-input"
            type="number"
            min="1"
            step="1"
            value={cleanupDaysInput}
            onChange={(e) => setCleanupDaysInput(e.target.value)}
            className="model-registry__filter-input"
            style={{ maxWidth: 180, marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={actionLoading}
              onClick={() => {
                const days = Number(cleanupDaysInput);
                if (!Number.isFinite(days) || days <= 0) {
                  setActionFeedback({ type: 'error', message: 'Please enter a valid number of days greater than 0.' });
                  return;
                }
                setShowCleanupPrompt(false);
                executeCleanupOldModels(days);
              }}
            >
              Run Cleanup
            </button>
            <button
              type="button"
              className="btn btn--outline"
              disabled={actionLoading}
              onClick={() => setShowCleanupPrompt(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="model-registry__filters">
        <div className="model-registry__filters-grid">
          <input
            type="text"
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              resetPagination();
            }}
            className="model-registry__filter-input"
          />
          <select
            value={filterDataset}
            onChange={(e) => {
              setFilterDataset(e.target.value);
              resetPagination();
            }}
            className="model-registry__filter-select"
          >
            <option value="all">All Datasets</option>
            {uniqueDatasets.map(dataset => (
              <option key={dataset} value={dataset}>{dataset}</option>
            ))}
          </select>
          <select
            value={filterModelType}
            onChange={(e) => {
              setFilterModelType(e.target.value);
              resetPagination();
            }}
            className="model-registry__filter-select"
          >
            <option value="all">All Model Types</option>
            {uniqueModelTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="model-registry__filter-select"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="id-asc">ID A-Z</option>
            <option value="id-desc">ID Z-A</option>
            <option value="dataset_name-asc">Dataset A-Z</option>
            <option value="file_size_mb-desc">Largest First</option>
            <option value="accuracy-desc">Highest Accuracy</option>
          </select>
          {/* Trash is handled in a separate dedicated view */}
        </div>
      </div>

      {/* Models Table */}
      {totalModels === 0 ? (
        <EmptyState 
          title="No models found"
          description="Try adjusting your search or filters"
        />
      ) : (
        <>
          <div className="model-registry__table-container">
            <table className="model-registry__table">
              <thead>
                <tr className="model-registry__table-header">
                  <th className="model-registry__table-header-cell">Model ID</th>
                  <th className="model-registry__table-header-cell">Dataset</th>
                  <th className="model-registry__table-header-cell">Type</th>
                  <th className="model-registry__table-header-cell">Status</th>
                  <th className="model-registry__table-header-cell">Created</th>
                  <th className="model-registry__table-header-cell">Size</th>
                  <th className="model-registry__table-header-cell">Accuracy</th>
                  <th className="model-registry__table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentModels.map((model) => (
                <tr key={model.id} className={`model-registry__table-row ${isSelected(model.id) ? 'model-registry__table-row--selected' : ''}`}>
                  <td className="model-registry__table-cell model-registry__table-cell--id">{model.id}</td>
                  <td className="model-registry__table-cell">{model.dataset_name}</td>
                  <td className="model-registry__table-cell">
                    <span className="model-registry__model-type">
                      {model.model_type}
                    </span>
                  </td>
                  <td className="model-registry__table-cell">{getModelStatusBadge(model)}</td>
                  <td className="model-registry__table-cell model-registry__table-cell--date">{formatDate(model.created_at)}</td>
                  <td className="model-registry__table-cell model-registry__table-cell--size">{formatFileSize(model.file_size_mb || 0)}</td>
                  <td className="model-registry__table-cell model-registry__table-cell--accuracy">
                    {model.metrics?.accuracy ? 
                      `${(model.metrics.accuracy * 100).toFixed(1)}%` : 
                      model.metrics?.r2_score ? 
                      `R²: ${model.metrics.r2_score.toFixed(3)}` : 
                      'N/A'
                    }
                  </td>
                  <td className="model-registry__table-cell">
                    <div className="model-registry__table-actions">
                      <button
                        type="button"
                        onClick={() => handleSelectModel(model)}
                        className={`model-registry__action-btn model-registry__action-btn--select ${isSelected(model.id) ? 'is-selected' : ''}`}
                        title="Set Active"
                      >
                        {isSelected(model.id) ? 'Active' : 'Select'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSelectModel(model)}
                        className="model-registry__action-btn model-registry__action-btn--view"
                        title="View Details Panel"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteModel(model.id)}
                        className="model-registry__action-btn model-registry__action-btn--delete"
                        title="Delete Model"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => handleValidateSingleModel(model.id)}
                        className="model-registry__action-btn model-registry__action-btn--view"
                        title="Validate this model only"
                        disabled={validatingModelId === model.id || actionLoading}
                      >
                        {validatingModelId === model.id ? 'Validating…' : 'Validate'}
                      </button>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="model-registry__pagination">
              <div className="model-registry__pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, totalModels)} of {totalModels} models
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </div>
              
              <div className="model-registry__pagination-controls">
                {/* Previous button */}
                <button
                  type="button"
                  onClick={goToPrevious}
                  disabled={currentPage === 1}
                  className={`model-registry__pagination-btn ${
                    currentPage === 1
                      ? 'model-registry__pagination-btn--disabled'
                      : 'model-registry__pagination-btn--prev'
                  }`}
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                <div className="model-registry__pagination-pages">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Adjust start if we're near the end
                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    // Show first page if not in range
                    if (startPage > 1) {
                      pages.push(
                        <button
                          type="button"
                          key={1}
                          onClick={() => goToPage(1)}
                          className="model-registry__pagination-page"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="start-ellipsis" className="model-registry__pagination-ellipsis">...</span>
                        );
                      }
                    }
                    
                    // Show page range
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          type="button"
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`model-registry__pagination-page ${
                            currentPage === i
                              ? 'model-registry__pagination-page--active'
                              : ''
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Show last page if not in range
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="end-ellipsis" className="model-registry__pagination-ellipsis">...</span>
                        );
                      }
                      pages.push(
                        <button
                          type="button"
                          key={totalPages}
                          onClick={() => goToPage(totalPages)}
                          className="model-registry__pagination-page"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                {/* Next button */}
                <button
                  type="button"
                  onClick={goToNext}
                  disabled={currentPage === totalPages}
                  className={`model-registry__pagination-btn ${
                    currentPage === totalPages
                      ? 'model-registry__pagination-btn--disabled'
                      : 'model-registry__pagination-btn--next'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Details Panel (always render when activeModel exists) */}
      {activeModel && (
        <div className="model-registry__details">
          <h3 className="model-registry__details-title">Selected Model Details</h3>
          <div className="model-registry__details-grid">
            <div className="model-registry__details-item"><span>ID:</span> {activeModel.id}</div>
            <div className="model-registry__details-item"><span>Dataset:</span> {activeModel.dataset_name}</div>
            <div className="model-registry__details-item"><span>Type:</span> {activeModel.model_type}</div>
            {activeModel.version && (
              <div className="model-registry__details-item"><span>Version:</span> {String(activeModel.version)}</div>
            )}
            <div className="model-registry__details-item"><span>Created:</span> {formatDate(activeModel.created_at)}</div>
            {activeModel.training_timestamp && (
              <div className="model-registry__details-item"><span>Trained:</span> {formatDate(activeModel.training_timestamp)}</div>
            )}
            <div className="model-registry__details-item"><span>Status:</span> {activeModel.status || '—'}</div>
            {activeModel.file_size_mb != null && (
              <div className="model-registry__details-item"><span>Size:</span> {formatFileSize(activeModel.file_size_mb)}</div>
            )}
            {/* Metadata additions (only what backend provides) */}
            {activeModel.normalization_method && (
              <div className="model-registry__details-item" data-testid="meta-normalization">
                <span>
                  Normalization: {activeModel.normalization_method}
                  <InfoTooltip 
                    title="Normalization method"
                    content="Technique used to scale features to a comparable range (e.g., Z-Score, Min-Max)."
                    position="top-right"
                  />
                </span>
              </div>
            )}
            {activeModel.batch_correction_method && (
              <div className="model-registry__details-item">
                <span>
                  Batch Correction: {activeModel.batch_correction_method}
                  <InfoTooltip 
                    title="Batch correction"
                    content="Method used to correct batch effects across datasets."
                    position="top-right"
                  />
                </span>
              </div>
            )}
            {activeModel.preprocessing_steps && (
              <div className="model-registry__details-item">
                <span>Preprocessing:</span>
                {Array.isArray(activeModel.preprocessing_steps) ? (
                  <ol className="model-registry__details-list">
                    {activeModel.preprocessing_steps.map((step, idx) => (
                      <li key={`prep-${idx}`}>{String(step)}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="model-registry__details-text">{String(activeModel.preprocessing_steps)}</div>
                )}
              </div>
            )}
            {activeModel.feature_set && (
              <div className="model-registry__details-item">
                <span>Feature Set:</span>
                {Array.isArray(activeModel.feature_set) ? (
                  <ol className="model-registry__details-list">
                    {activeModel.feature_set.map((feat, idx) => (
                      <li key={`feat-${idx}`}>{String(feat)}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="model-registry__details-text">{String(activeModel.feature_set)}</div>
                )}
              </div>
            )}
            {activeModel.data_signature && (
              <div className="model-registry__details-item">
                <span>Data Signature:</span>
                <code title={String(activeModel.data_signature)} style={{ marginLeft: 6 }}>
                  {String(activeModel.data_signature).length > 16 
                    ? `${String(activeModel.data_signature).slice(0, 16)}…` 
                    : String(activeModel.data_signature)}
                </code>
              </div>
            )}
          </div>
          {/* Hyperparameters (e.g. neural network) */}
          {activeModel.hyperparameters && Object.keys(activeModel.hyperparameters).length > 0 && (
            <div className="model-registry__hyperparameters" style={{ marginTop: 16 }}>
              <h4>Hyperparameters</h4>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 13, margin: 0 }}>
                {Object.entries(activeModel.hyperparameters).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <dt style={{ margin: 0, color: '#555' }}>{key.replace(/_/g, ' ')}</dt>
                    <dd style={{ margin: 0 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}
          <div className="model-registry__details-actions" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button 
              type="button"
              className="btn btn--secondary"
              onClick={handleExportReport}
              disabled={exporting || !activeModel?.id}
              title={activeModel?.id ? `Export HTML report for ${activeModel.id}` : 'Select a model first'}
            >
              {exporting ? 'Exporting…' : 'Export HTML Report'}
            </button>
            <button 
              type="button"
              className="btn btn--primary"
              onClick={handleExportPdfReport}
              disabled={exporting || !activeModel?.id}
              title={activeModel?.id ? `Export PDF report for ${activeModel.id}` : 'Select a model first'}
            >
              {exporting ? 'Exporting…' : 'Export PDF Report'}
            </button>
          </div>
          {/* Evaluation summary (interpretable model behavior) */}
          <div className="model-registry__evaluation-summary" style={{ marginTop: 16 }}>
            <h4>Evaluation summary</h4>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              Trust higher-confidence models with clean validation status, low false-negative risk, and stable threshold behavior.
            </p>
            {evaluationLoading ? (
              <p style={{ color: '#666', fontSize: 13 }}>Loading…</p>
            ) : evaluationSummary ? (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 13, fontFamily: 'inherit' }}>{evaluationSummary}</pre>
            ) : (
              <p style={{ color: '#666', fontSize: 13 }}>No summary available.</p>
            )}
          </div>
          {/* Evaluation flags (overfitting, underfitting, FNR risk, etc.) */}
          {evaluationFlags.length > 0 && (
            <div className="model-registry__evaluation-flags" style={{ marginTop: 16 }}>
              <h4>Evaluation flags</h4>
              <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Prioritize fixing high-risk flags before rollout (especially missing files, corruption, and high false negatives).
              </p>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                {evaluationFlags.map((flag, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>{flag}</li>
                ))}
              </ul>
            </div>
          )}
          {(activeModel.metadata?.task_type === 'classification' || activeModel.task_type === 'classification') && (
            <div className="model-registry__details-threshold" style={{ marginTop: 16 }}>
              <h4>Decision threshold (positive class)</h4>
              <p className="model-registry__threshold-note" style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Adjust the cutoff for predicting positive (e.g. Alzheimer&apos;s-positive). Changing this does not require retraining.
              </p>
              <p className="model-registry__threshold-note" style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Use lower thresholds for higher sensitivity (catch more positives) and higher thresholds for higher precision (fewer false positives).
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={decisionThreshold ?? 0.5}
                  onChange={(e) => setDecisionThreshold(Number(e.target.value))}
                  style={{ width: 120 }}
                />
                <span style={{ minWidth: 48 }}>{(decisionThreshold ?? 0.5).toFixed(2)}</span>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={thresholdUpdating || !activeModel?.id}
                  onClick={async () => {
                    if (activeModel?.id == null || decisionThreshold == null) return;
                    setThresholdUpdating(true);
                    try {
                      await updateDecisionThreshold(activeModel.id, decisionThreshold);
                      await loadRegistryData();
                      const updated = (await getModelInfo(activeModel.id)) || activeModel;
                      setActiveModel(updated);
                    } catch (err) {
                      setActionFeedback({
                        type: 'error',
                        message: err.message || 'Failed to update threshold',
                      });
                    } finally {
                      setThresholdUpdating(false);
                    }
                  }}
                >
                  {thresholdUpdating ? 'Updating…' : 'Update threshold'}
                </button>
              </div>
            </div>
          )}
          {(() => {
            const thresholdMetrics = activeModel.metadata?.evaluation_artifacts?.threshold_metrics || activeModel.threshold_metrics;
            if (!thresholdMetrics || !Array.isArray(thresholdMetrics) || thresholdMetrics.length === 0) return null;
            return (
              <div className="model-registry__threshold-metrics" style={{ marginTop: 16 }}>
                <h4>Metrics vs threshold (positive class / Alzheimer&apos;s-positive)</h4>
                <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  Sensitivity (recall), false-negative rate, and precision for class 1 as the decision threshold changes.
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: 6, textAlign: 'left' }}>Threshold</th>
                        <th style={{ padding: 6, textAlign: 'right' }}>Sensitivity</th>
                        <th style={{ padding: 6, textAlign: 'right' }}>Recall (pos)</th>
                        <th style={{ padding: 6, textAlign: 'right' }}>FNR</th>
                        <th style={{ padding: 6, textAlign: 'right' }}>Precision (pos)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {thresholdMetrics.map((row) => (
                        <tr key={row.threshold} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: 6 }}>{row.threshold}</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>{(row.sensitivity * 100).toFixed(1)}%</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>{(row.recall_positive * 100).toFixed(1)}%</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>{(row.false_negative_rate * 100).toFixed(1)}%</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>{(row.precision_positive * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          {/* Positive class impact (Alzheimer's / class 1) for binary classification */}
          {(activeModel.metadata?.task_type === 'classification' || activeModel.task_type === 'classification') && activeModel.metrics?.confusion_matrix && (() => {
            const cm = activeModel.metrics.confusion_matrix;
            const fn = cm.false_negatives ?? 0;
            const tp = cm.true_positives ?? 0;
            const totalPos = fn + tp;
            const fnr = totalPos > 0 ? (fn / totalPos) : 0;
            return (
              <div className="model-registry__positive-class-impact" style={{ marginTop: 16 }}>
                <h4>Positive class impact (Alzheimer&apos;s-positive)</h4>
                <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  False negatives are missed positive cases; high FNR is critical in screening.
                </p>
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 13, margin: 0 }}>
                  <dt style={{ margin: 0, color: '#555' }}>False negatives</dt>
                  <dd style={{ margin: 0 }}>{fn}</dd>
                  <dt style={{ margin: 0, color: '#555' }}>True positives</dt>
                  <dd style={{ margin: 0 }}>{tp}</dd>
                  <dt style={{ margin: 0, color: '#555' }}>False negative rate (FNR)</dt>
                  <dd style={{ margin: 0 }}>{(fnr * 100).toFixed(1)}%</dd>
                </dl>
              </div>
            );
          })()}
          {activeModel.metrics && (
            <div className="model-registry__details-metrics">
              <h4>Metrics</h4>
              <pre>{JSON.stringify(activeModel.metrics, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {/* Summary when no pagination needed */}
      {totalPages <= 1 && totalModels > 0 && (
        <div className="model-registry__summary">
          Showing all {totalModels} models
        </div>
      )}
    </div>
  );
};

export default ModelRegistry;
